import React, { useMemo, useState } from 'react';
import {
  Split, SplitItem
} from '@patternfly/react-core';
import _ from 'lodash';

import { sortLocale } from '../../utils/string';
import Markdown from './Markdown';
import Navigation from './Navigation';
import { remark } from 'remark';
import { toString } from 'mdast-util-to-string';
import styles from './HelpView.module.css';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
const dirname = filename => filename?.substring(0, filename?.lastIndexOf('/'));

const languageRegEx = /^\.\/([a-z]*)\//;
const absLanguageRegEx = /^docs\/([a-z]*)\//;

const addHeading = (result, markdownObject, key) => {
  // Find first heading in markdown and use it as heading for the file
  let firstHeading = null;
  remark()
    .use(() => {
      return (root) => {
        if (firstHeading == null) {
          _.forEach(root.children, child => {
            if (child.type === 'heading' && child.depth === 1) {
              firstHeading = toString(child);
            }
          });
        }
      };
    })
    .processSync(markdownObject.value);

  return { ...result, [key]: { ...markdownObject, heading: firstHeading } };
};

const data = (function (context) {
  let keys = context.keys();
  const values = keys.map(context);

  return keys.reduce((result, key, index) => {
    const fullPath = key;
    var language;
    var newKey;
    let directory;
    // This is due to slowly moving towards absolute imports of components
    // TODO remove when all files are absolutly imported
    if (key.match(languageRegEx)) {
      language = key.match(languageRegEx)[1];
      newKey = key.replace('./' + language + '/', '');
      directory = dirname(fullPath).replace('./' + language + '/', '');
    } else if (key.match(absLanguageRegEx)) {
      language = key.match(absLanguageRegEx)[1];
      newKey = key.replace('docs/' + language + '/', '');
      directory = dirname(fullPath).replace('docs/' + language + '/', '');
    } else {
      console.error('we have another way of finding documents');
    }

    result[language] = {
      ...result[language],
      [newKey]: {
        value: values[index],
        directory
      }
    };
    return result;
  }, {});
}(require.context('/src/docs/', true, /\.md$/)));

// includes all .md-files to the bundle.
const markdownFiles = _.reduce(data,
  (result, markdowns, key) => {
    result[key] = _.reduce(markdowns, addHeading, {});
    return result;
  }, {});


const getMarkdownFiles = (filename, language = 'en') => {
  const languageMarkdownFiles = markdownFiles[language];
  const singleMarkdownFile = languageMarkdownFiles[filename];

  if (singleMarkdownFile == null) {
    return _.reduce(languageMarkdownFiles, (result, file, key) => {
      if (_.startsWith(file.directory, filename)) {
        result[key] = file;
      }
      return result;
    }, {});
  }

  return { [filename]: singleMarkdownFile };
};

export const hasHelpFile = (filename, language) => {
  return !_.isEmpty(getMarkdownFiles(filename, language));
};

const HelpView = ({ file, className }) => {
  const { i18n } = useTranslation();

  const [markdowns, files, headings] = useMemo(() => {
    const markdowns = getMarkdownFiles(file, i18n.language);

    const keysAndHeadings = _.map(markdowns, ({ heading }, key) => ({ heading, key }));

    const sortedKeysAndHeadings = sortLocale(keysAndHeadings, 'heading');
    return [markdowns, _.map(sortedKeysAndHeadings, 'key'), _.map(sortedKeysAndHeadings, 'heading')];
  }, [file, i18n.language]);

  const [activeItem, setActiveItem] = useState(0);
  const markdown = markdowns[files[activeItem]]?.value;

  return (<Split hasGutter className={className}>
    { _.size(files) > 1 &&
      (<SplitItem className={styles.helpNavigation}>
        <Navigation items={headings} activeItem={activeItem} onItemSelected={setActiveItem} />
      </SplitItem>) }
    <SplitItem isFilled>
      <Markdown markdown={markdown} />
    </SplitItem>
  </Split>);

};

HelpView.propTypes = {
  file: PropTypes.string.isRequired,
  className: PropTypes.string
};

export default React.memo(HelpView);
